import React from 'react';
import { clsx } from 'clsx';
import { AlertTriangle, CalendarClock, Repeat } from 'lucide-react';
import type { Activity, Priority } from '@/types/activity';
import { PRIORITY_BADGE, PRIORITY_LABELS } from '@/utils/priority';
import { formatDateBR, formatDayMonthBR, initials } from '@/utils/formatters';

/** Badge de prioridade (Baixa/Média/Alta/Crítica). */
export const PriorityBadge: React.FC<{ priority: Priority; className?: string }> = ({ priority, className }) => (
  <span
    className={clsx(
      'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap',
      PRIORITY_BADGE[priority],
      className,
    )}
  >
    {priority === 'critical' && <AlertTriangle size={10} />}
    {PRIORITY_LABELS[priority]}
  </span>
);

/** Avatar com as iniciais do responsável (cinza tracejado = sem responsável). */
export const Avatar: React.FC<{ name?: string | null; size?: 'sm' | 'md'; className?: string }> = ({
  name,
  size = 'sm',
  className,
}) => (
  <span
    title={name || 'Sem responsável'}
    className={clsx(
      'inline-flex items-center justify-center rounded-full font-semibold flex-shrink-0',
      size === 'sm' ? 'w-6 h-6 text-[10px]' : 'w-8 h-8 text-xs',
      name ? 'gradient-user-avatar text-white' : 'border border-dashed border-slate-300 text-slate-400 bg-white',
      className,
    )}
  >
    {name ? initials(name) : '?'}
  </span>
);

/** Prazo da atividade: vermelho se atrasada, "Sem prazo" em âmbar. */
export const DueDateLabel: React.FC<{ activity: Activity; short?: boolean; className?: string }> = ({
  activity,
  short = false,
  className,
}) => {
  if (!activity.due_date) {
    return (
      <span className={clsx('inline-flex items-center gap-1 text-xs text-amber-600', className)}>
        <CalendarClock size={12} />
        Sem prazo
      </span>
    );
  }
  const overdue = activity.is_overdue && !activity.is_done;
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 text-xs',
        overdue ? 'text-red-600 font-semibold' : 'text-slate-500',
        className,
      )}
      title={overdue ? 'Atrasada' : undefined}
    >
      <CalendarClock size={12} />
      {short ? formatDayMonthBR(activity.due_date) : formatDateBR(activity.due_date)}
    </span>
  );
};

/** Ícone de atividade gerada por recorrência. */
export const RecurrenceIcon: React.FC<{ activity: Activity }> = ({ activity }) =>
  activity.recurrence_rule_id ? (
    <span title="Atividade recorrente" className="text-slate-400">
      <Repeat size={12} />
    </span>
  ) : null;

/** Linha compacta de atividade para as listas do dashboard. */
export const ActivityRow: React.FC<{ activity: Activity; onClick: () => void; showAssignee?: boolean }> = ({
  activity,
  onClick,
  showAssignee = true,
}) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-3"
  >
    {showAssignee && <Avatar name={activity.assignee_name} />}
    <div className="flex-1 min-w-0">
      <p className={clsx('text-sm text-slate-800 truncate', activity.is_done && 'line-through text-slate-400')}>
        {activity.title}
      </p>
      <p className="text-xs text-slate-400 truncate">
        {activity.board_name} · {activity.column_name}
      </p>
    </div>
    <div className="flex flex-col items-end gap-1 flex-shrink-0">
      <PriorityBadge priority={activity.priority} />
      <DueDateLabel activity={activity} />
    </div>
  </button>
);
